/**
 * Signed-in accounts, in the browser.
 *
 * GoTrue is served from the same origin under /auth/v1, so there is no CORS
 * preflight, no second domain for Apple to verify, and no SDK — the three calls
 * this app makes are plain fetches.
 *
 * The tokens live in localStorage rather than a cookie because the API reads
 * them from an Authorization header. That is a deliberate trade: a cookie would
 * be immune to XSS but would also be sent on every request to every path,
 * including the ones GoTrue itself serves, and this app has no server-side
 * session to pair it with.
 */

const STORE_KEY = 'waves.auth.session';

/*
 * Listeners for "the account session died".
 *
 * A refused refresh used to clear the tokens and return null, and the app simply
 * carried on — straight onto the legacy enrolled-device cookie, which still
 * carries operator rights for a year. So a lapsed sign-in did not look like a
 * lapsed sign-in: the host screen worked, "Go live" worked, and the show was
 * filed under the old anonymous subject instead of the account. Nothing anywhere
 * said so.
 */
const sessionLostListeners = new Set<() => void>();

export function onSessionLost(listener: () => void): () => void {
  sessionLostListeners.add(listener);
  return () => sessionLostListeners.delete(listener);
}

function announceSessionLost(): void {
  for (const listener of sessionLostListeners) listener();
}
const AUTH_BASE = '/auth/v1';

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. Local clock — only ever used to refresh EARLY, never to trust. */
  expiresAt: number;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

function read(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    return parsed.accessToken && parsed.refreshToken ? parsed : null;
  } catch {
    return null;
  }
}

function write(session: AuthSession | null): void {
  try {
    if (session) localStorage.setItem(STORE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORE_KEY);
  } catch {
    /* Private mode with storage denied. Sign-in will not persist; nothing else
       breaks, and the anonymous path still works. */
  }
}

const sessionFrom = (body: Record<string, unknown>): AuthSession | null => {
  const accessToken = typeof body.access_token === 'string' ? body.access_token : '';
  const refreshToken = typeof body.refresh_token === 'string' ? body.refresh_token : '';
  if (!accessToken || !refreshToken) return null;
  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : 3600;
  return { accessToken, refreshToken, expiresAt: Date.now() + expiresIn * 1000 };
};

/**
 * Capture the tokens GoTrue leaves in the URL fragment after a provider redirect.
 *
 * The fragment is stripped immediately. It never reaches a server by itself, but
 * it does land in history and in anything that later reads location.href, and
 * these are bearer credentials.
 */
export async function captureRedirect(): Promise<AuthSession | null> {
  const url = new URL(window.location.href);

  /*
   * PKCE first: GoTrue returns `?code=` in the QUERY, not tokens in the fragment.
   *
   * This is the default flow in GoTrue v2 and it is what broke sign-in for both
   * hosts — the callback succeeded, GoTrue logged the login, and this function
   * looked only at the fragment, found nothing, and left them staring at the
   * login page holding a valid authorization code.
   */
  const code = url.searchParams.get('code');
  if (code) {
    url.searchParams.delete('code');
    // A code is a credential. Out of the address bar before anything can read it.
    window.history.replaceState({}, '', url.pathname + url.search);
    try {
      const response = await fetch(`${AUTH_BASE}/token?grant_type=pkce`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ auth_code: code }),
      });
      if (response.ok) {
        const session = sessionFrom((await response.json()) as Record<string, unknown>);
        if (session) {
          write(session);
          return session;
        }
      }
    } catch {
      /* Falls through to the fragment path, then to a visible failure. */
    }
  }

  /*
   * Implicit fallback: tokens in the fragment.
   *
   * Kept because it is what a differently-configured GoTrue returns, and the two
   * cost nothing to support together — whereas assuming one of them is exactly
   * how this broke.
   */
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const session = sessionFrom({
    access_token: params.get('access_token') ?? undefined,
    refresh_token: params.get('refresh_token') ?? undefined,
    expires_in: Number(params.get('expires_in')) || 3600,
  });

  const error = params.get('error_description') ?? params.get('error');
  if (session || error) {
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
  }
  if (session) write(session);
  return session;
}

/**
 * Does this page load look like a return trip from the provider?
 *
 * Cheap and synchronous, so the app can decide to run the auth path before doing
 * anything else — including on the root, where GoTrue actually lands.
 */
export function pendingAuthRedirect(): boolean {
  const query = new URL(window.location.href).searchParams;
  const fragment = window.location.hash.startsWith('#')
    ? new URLSearchParams(window.location.hash.slice(1))
    : new URLSearchParams();
  for (const key of ['code', 'access_token', 'error', 'error_description']) {
    if (query.get(key) || fragment.get(key)) return true;
  }
  return false;
}

/** Any error the provider redirect came back with, for showing to the user. */
export function redirectError(): string | null {
  // Either half of the URL, since the two flows report failure differently.
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  for (const params of [new URLSearchParams(hash), new URL(window.location.href).searchParams]) {
    const message = params.get('error_description') ?? params.get('error');
    if (message) return decodeURIComponent(message.replace(/\+/g, ' '));
  }
  return null;
}

/**
 * A usable access token, refreshing if it is close to expiry.
 *
 * Refreshes with a minute to spare rather than on expiry: a token that dies
 * between the check and the request it was fetched for is a logout in the middle
 * of a show, and the whole point of accounts is that that stops happening.
 */
export async function accessToken(): Promise<string | null> {
  const current = read();
  if (!current) return null;
  if (Date.now() < current.expiresAt - 60_000) return current.accessToken;

  try {
    const response = await fetch(`${AUTH_BASE}/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: current.refreshToken }),
    });
    if (!response.ok) {
      // A refused refresh token is gone for good — clear it rather than retrying
      // on every call for the rest of the session.
      write(null);
      return null;
    }
    const next = sessionFrom((await response.json()) as Record<string, unknown>);
    write(next);
    return next?.accessToken ?? null;
  } catch {
    // Offline. Keep the session and use what we have; the API will 401 if it is
    // genuinely dead, and that is recoverable.
    return current.accessToken;
  }
}

export const isSignedIn = (): boolean => read() !== null;

/** Start Sign in with Apple. A full-page redirect, not a popup: iOS blocks those. */
export function signInWithApple(returnTo: string = window.location.pathname): void {
  const target = new URL(`${AUTH_BASE}/authorize`, window.location.origin);
  target.searchParams.set('provider', 'apple');
  target.searchParams.set('redirect_to', new URL(returnTo, window.location.origin).toString());
  window.location.assign(target.toString());
}

/** Forget this device. Other devices keep their own sessions. */
export async function signOut(): Promise<void> {
  const current = read();
  write(null);
  if (!current) return;
  try {
    await fetch(`${AUTH_BASE}/logout`, {
      method: 'POST',
      headers: { authorization: `Bearer ${current.accessToken}` },
    });
  } catch {
    /* Local sign-out already happened, which is the part that matters. */
  }
}

/** Which providers this deployment actually has configured. */
export async function enabledProviders(): Promise<string[]> {
  try {
    const response = await fetch(`${AUTH_BASE}/settings`);
    if (!response.ok) return [];
    const body = (await response.json()) as { external?: Record<string, boolean> };
    return Object.entries(body.external ?? {})
      .filter(([, on]) => on)
      .map(([name]) => name);
  } catch {
    return [];
  }
}
